import { Stack } from '@fluentui/react';
import { useTheme } from '@h2oai/ui-kit';
import { useEffect, useState } from 'react';

import { GaugeMetricDailyStatRequest } from '../../../../../admin-center/gen/mlops/gauge_metric/gaugemetric_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { MLOpsGaugeType } from '../../../constants';
import StatsCard from '../../StatsCard/StatsCard';
import { GaugeMetricStatContainerStyle } from '../MLOps.styles';

interface GaugeMetricSummaryProps {
  date: string;
}

interface GaugeMetricStatsData {
  stats: {
    user: number;
    project: number;
    dataset: number;
    experiment: number;
    'registered-model': number;
    'registered-model-version': number;
    deployment: number;
  };
}

export default function GaugeMetricStats({ date }: GaugeMetricSummaryProps) {
  const service = useAdminCenterService();
  const theme = useTheme();
  const [statData, setStatData] = useState<GaugeMetricStatsData | null>(null);
  const [statLoading, setLoading] = useState<boolean>(true);
  const [statError, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGaugeMetricStats = async () => {
      setLoading(true);
      try {
        const request: GaugeMetricDailyStatRequest = {
          date: date,
        };
        const response = await service.getMlopsGaugeDailyMetrics(request);
        setStatData(response as unknown as GaugeMetricStatsData);
      } catch (error) {
        console.error('Error fetching Gauge Metric events:', error);
        setError('Error fetching Gauge Metric events');
      } finally {
        setLoading(false);
      }
    };

    fetchGaugeMetricStats();
  }, [date, service]);

  const statsCards = [
    {
      title: MLOpsGaugeType.user,
      stat: statData?.stats?.user ?? 0,
    },
    {
      title: MLOpsGaugeType.project,
      stat: statData?.stats?.project ?? 0,
    },
    {
      title: MLOpsGaugeType.dataset,
      stat: statData?.stats?.dataset ?? 0,
    },
    {
      title: MLOpsGaugeType.experiment,
      stat: statData?.stats?.experiment ?? 0,
    },
    {
      title: MLOpsGaugeType.registeredModel,
      stat: statData?.stats?.['registered-model'] ?? 0,
    },
    {
      title: MLOpsGaugeType.registeredModelVersion,
      stat: statData?.stats?.['registered-model-version'] ?? 0,
    },
    {
      title: MLOpsGaugeType.deployment,
      stat: statData?.stats?.deployment ?? 0,
    },
  ];

  if (statError) {
    return (
      <div style={GaugeMetricStatContainerStyle(theme)}>
        <div style={{ color: theme.semanticColors?.inputErrorMessageText, padding: '16px' }}>{statError}</div>
      </div>
    );
  }

  return (
    <div style={GaugeMetricStatContainerStyle(theme)}>
      <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
        {statsCards.map((card) => (
          <StatsCard key={card.title} title={card.title} stat={card.stat} isLoading={statLoading} />
        ))}
      </Stack>
    </div>
  );
}
